import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  LinearProgress,
  Alert,
  CircularProgress,
  Chip,
  FormControlLabel,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  EmojiEvents as TrophyIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import { MainLayout } from '../components/MainLayout';
import {
  GoalsService,
  type Goal,
  type GoalContribution,
} from '../services/goals-service';

const DEFAULT_COLORS = ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336', '#00BCD4', '#FF5722', '#795548'];

export const GoalsPage: React.FC = () => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeCompleted, setIncludeCompleted] = useState(false);

  // Create/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [formName, setFormName] = useState('');
  const [formTarget, setFormTarget] = useState('');
  const [formDate, setFormDate] = useState('');
  const [formColor, setFormColor] = useState('#4CAF50');
  const [formNotes, setFormNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Contribution dialog
  const [contribDialogOpen, setContribDialogOpen] = useState(false);
  const [contribGoalId, setContribGoalId] = useState<string | null>(null);
  const [contribAmount, setContribAmount] = useState('');
  const [contribDate, setContribDate] = useState(new Date().toISOString().split('T')[0]);
  const [contribNotes, setContribNotes] = useState('');

  // History dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyGoalId, setHistoryGoalId] = useState<string | null>(null);
  const [contributions, setContributions] = useState<GoalContribution[]>([]);
  const [loadingContribs, setLoadingContribs] = useState(false);

  const fetchGoals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await GoalsService.listGoals(includeCompleted);
      setGoals(res.goals || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load goals');
    } finally {
      setLoading(false);
    }
  }, [includeCompleted]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  const openCreateDialog = () => {
    setEditingGoal(null);
    setFormName('');
    setFormTarget('');
    setFormDate('');
    setFormColor(DEFAULT_COLORS[goals.length % DEFAULT_COLORS.length]);
    setFormNotes('');
    setDialogOpen(true);
  };

  const openEditDialog = (goal: Goal) => {
    setEditingGoal(goal);
    setFormName(goal.name);
    setFormTarget(goal.target_amount);
    setFormDate(goal.target_date || '');
    setFormColor(goal.color || '#4CAF50');
    setFormNotes(goal.notes || '');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingGoal) {
        await GoalsService.updateGoal(editingGoal.id, {
          name: formName,
          target_amount: formTarget,
          target_date: formDate || undefined,
          color: formColor,
          notes: formNotes || undefined,
        });
      } else {
        await GoalsService.createGoal({
          name: formName,
          target_amount: formTarget,
          target_date: formDate || undefined,
          color: formColor,
          notes: formNotes || undefined,
        });
      }
      setDialogOpen(false);
      fetchGoals();
    } catch (err: any) {
      setError(err.message || 'Failed to save goal');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this goal? This cannot be undone.')) return;
    try {
      await GoalsService.deleteGoal(id);
      fetchGoals();
    } catch (err: any) {
      setError(err.message || 'Failed to delete goal');
    }
  };

  const openContribDialog = (goalId: string) => {
    setContribGoalId(goalId);
    setContribAmount('');
    setContribDate(new Date().toISOString().split('T')[0]);
    setContribNotes('');
    setContribDialogOpen(true);
  };

  const handleAddContribution = async () => {
    if (!contribGoalId || !contribAmount) return;
    setSaving(true);
    try {
      await GoalsService.addContribution(contribGoalId, {
        amount: contribAmount,
        date: contribDate,
        notes: contribNotes || undefined,
      });
      setContribDialogOpen(false);
      fetchGoals();
    } catch (err: any) {
      setError(err.message || 'Failed to add contribution');
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (goalId: string) => {
    setHistoryGoalId(goalId);
    setHistoryDialogOpen(true);
    setLoadingContribs(true);
    try {
      const res = await GoalsService.listContributions(goalId);
      setContributions(res.contributions || []);
    } catch {
      setContributions([]);
    } finally {
      setLoadingContribs(false);
    }
  };

  const handleDeleteContribution = async (contribId: string) => {
    if (!historyGoalId) return;
    try {
      await GoalsService.deleteContribution(historyGoalId, contribId);
      // Refresh contributions and goals
      const res = await GoalsService.listContributions(historyGoalId);
      setContributions(res.contributions || []);
      fetchGoals();
    } catch (err: any) {
      setError(err.message || 'Failed to delete contribution');
    }
  };

  return (
    <MainLayout>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">Goals</Typography>
            <Typography variant="body2" color="text.secondary">
              Set savings targets and track your progress
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControlLabel
              control={<Checkbox checked={includeCompleted} onChange={(e) => setIncludeCompleted(e.target.checked)} size="small" />}
              label={<Typography variant="body2">Show completed</Typography>}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
              New Goal
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : goals.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <FlagIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>No goals yet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create your first savings goal to start tracking progress
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
                Create Goal
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 3 }}>
            {goals.map((goal) => {
              const pct = parseFloat(goal.percent_complete);
              const current = parseFloat(goal.current_amount);
              const target = parseFloat(goal.target_amount);
              const color = goal.color || '#4CAF50';
              const progressColor = pct >= 100 ? 'success' : pct >= 75 ? 'warning' : 'primary';

              return (
                <Card key={goal.id} sx={{ position: 'relative', borderTop: `4px solid ${color}` }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="h6" fontWeight={600}>{goal.name}</Typography>
                          {goal.is_completed && (
                            <Chip icon={<TrophyIcon />} label="Complete" color="success" size="small" />
                          )}
                        </Box>
                        {goal.target_date && (
                          <Typography variant="caption" color="text.secondary">
                            Target: {new Date(goal.target_date).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <IconButton size="small" onClick={() => openEditDialog(goal)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => handleDelete(goal.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </Box>
                    </Box>

                    {/* Progress */}
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>
                          ${current.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ${target.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(pct, 100)}
                        color={progressColor}
                        sx={{ height: 10, borderRadius: 5 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {pct}% complete | ${(target - current).toLocaleString(undefined, { minimumFractionDigits: 2 })} remaining
                      </Typography>
                    </Box>

                    {goal.notes && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                        {goal.notes}
                      </Typography>
                    )}

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button size="small" variant="outlined" onClick={() => openContribDialog(goal.id)}>
                        Add Contribution
                      </Button>
                      <Button size="small" onClick={() => openHistory(goal.id)}>
                        History
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingGoal ? 'Edit Goal' : 'New Goal'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField label="Name" value={formName} onChange={(e) => setFormName(e.target.value)} fullWidth required />
              <TextField label="Target Amount" value={formTarget} onChange={(e) => setFormTarget(e.target.value)} type="number" fullWidth required />
              <TextField label="Target Date" value={formDate} onChange={(e) => setFormDate(e.target.value)} type="date" InputLabelProps={{ shrink: true }} fullWidth />
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>Color</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {DEFAULT_COLORS.map((c) => (
                    <Box
                      key={c}
                      onClick={() => setFormColor(c)}
                      sx={{
                        width: 32, height: 32, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
                        border: formColor === c ? '3px solid' : '2px solid transparent',
                        borderColor: formColor === c ? 'text.primary' : 'transparent',
                      }}
                    />
                  ))}
                </Box>
              </Box>
              <TextField label="Notes" value={formNotes} onChange={(e) => setFormNotes(e.target.value)} multiline rows={2} fullWidth />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSave} disabled={saving || !formName || !formTarget}>
              {saving ? <CircularProgress size={20} /> : editingGoal ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Contribution Dialog */}
        <Dialog open={contribDialogOpen} onClose={() => setContribDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Add Contribution</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField label="Amount" value={contribAmount} onChange={(e) => setContribAmount(e.target.value)} type="number" fullWidth required />
              <TextField label="Date" value={contribDate} onChange={(e) => setContribDate(e.target.value)} type="date" InputLabelProps={{ shrink: true }} fullWidth />
              <TextField label="Notes" value={contribNotes} onChange={(e) => setContribNotes(e.target.value)} fullWidth />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setContribDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleAddContribution} disabled={saving || !contribAmount}>
              {saving ? <CircularProgress size={20} /> : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Contribution History Dialog */}
        <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Contribution History</DialogTitle>
          <DialogContent>
            {loadingContribs ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : contributions.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No contributions yet
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell width={40}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contributions.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>{new Date(c.date).toLocaleDateString()}</TableCell>
                      <TableCell align="right">${parseFloat(c.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                      <TableCell>{c.notes || '-'}</TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleDeleteContribution(c.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};
